import {
  IBillSale, ICreditDataDate, ICreditDataSale,
  IItemSale,
  ILotSale,
  IPaymentMethodSale,
  ISale,
  ISellerSale
} from '@/pages/point-of-sale/models/sale.model.ts';
import {
  ISaleAddModel, ISaleAddModelAdditionalData, ISaleAddModelBill, ISaleAddModelCreditData,
  ISaleAddModelCustomer, ISaleAddModelItem, ISaleAddModelLot, ISaleAddModelPaymentDate, ISaleAddModelPaymentMethod,
  ISaleAddModelSeller
} from '@/pages/point-of-sale/models/sale-add.model.ts';
import { ICustomer } from '@/models/customer.model.ts';
import { IAdditionalData } from '@/pages/point-of-sale/models/addition-data.model.ts';
import { roundMoney } from '@/utilities/decimal.utils';
import { normalizeSaleAddPayloadContainerTypes } from '@/pages/point-of-sale/utilities/container-type-resolve.utils';
import {
  ITEM_DISCOUNT_BY_AMOUNT,
  ITEM_DISCOUNT_BY_POINTS,
} from '@/pages/point-of-sale/utilities/sale-discount.utils';
import { useProductStore } from '@/store/product.store';

export const createSaleAddAdapter = (sale: ISale, offlineUuid?: string | null): ISaleAddModel => {
  const payload: ISaleAddModel = {
    cashier: sale.cashier,
    seller: createSaleAddSellerAdapter(sale.seller),
    bill: createSaleAddBillAdapter(sale.bill),
    customer: createSaleAddCustomerAdapter(sale.customer),
    additional_data: createSaleAddAdditionalDataAdapter(sale.additionalData),
    workshift: sale.workshift,
    creation_date: sale.creationDate,
    offline_uuid: offlineUuid ?? null,
  };
  normalizeSaleAddPayloadContainerTypes(
    payload,
    useProductStore.getState().products
  );
  return payload;
};

export const createSaleAddSellerAdapter = (seller: ISellerSale): ISaleAddModelSeller => ({
  id: seller.id,
  name: seller.name,
  last_name: seller.lastName,
});

export const createSaleAddCustomerAdapter = (customer: ICustomer): ISaleAddModelCustomer => ({
  id: customer.id,
  document_number: customer.documentNumber,
});

export const createSaleAddAdditionalDataAdapter = (additionalData: IAdditionalData): ISaleAddModelAdditionalData => ({
  document_type: additionalData.documentType,
  document_number: additionalData.documentNumber,
  purchase_order: additionalData.purchaseOrder,
  apply_retention: additionalData.applyRetention,
  apply_detraction: additionalData.applyDetraction,
  bank_account_number: additionalData.bankAccountNumber,
  detraction_percentage: additionalData.detractionPercentage,
  send_whatsapp: additionalData.sendWhatsapp,
  whatsapp_number: additionalData.whatsappNumber,
  send_email: additionalData.sendEmail,
  email: additionalData.email
});

export const createSaleAddBillAdapter = (bill: IBillSale): ISaleAddModelBill => ({
  bill_type: bill.billType,
  serial: bill.serial,
  correlative: bill.correlative,
  ...(bill.idSerie ? { id_serie: bill.idSerie } : {}),
  discount: bill.discount,
  discountType: bill.discountType,
  total_amount: roundMoney(bill.totalAmount),
  items: bill.items.map(createSaleAddItemAdapter),
  payment_type: bill.paymentType,
  loyaltySummary:bill.loyaltySummary,
  payment_methods: bill.paymentMethods !== null ? bill.paymentMethods.map(createSaleAddPaymentMethodAdapter) : null,
  credit_data: bill.creditData !== null ? createSaleAddCreditDataAdapter(bill.creditData) : null
});

export const createSaleAddItemAdapter = (item: IItemSale): ISaleAddModelItem => ({
  product_id: item.productId,
  lots: item.lots.map(createSaleAddLotAdapter),
  name: item.name,
  container_type: item.containerType,
  unit_price: item.unitPrice,
  total_quantity: item.quantity,
  total_price: item.totalPrice,
  vat_type: item.vatType,
  discount_type: item.discountType,           
  discounted_amount: item.discountedAmount,
  discount_amount: item.discountAmount > 0 ? item.discountAmount : undefined,
  points_redeemed: item.pointsRedeeemed,
  amount_redeemed: item.amountRedeemed ?? 0
});

export const createSaleAddLotAdapter = (lot: ILotSale): ISaleAddModelLot => ({
  id: lot.id,
  serial: lot.serial ?? "",
  quantity: lot.quantity,
});

export const createSaleAddPaymentMethodAdapter = (paymentMethod: IPaymentMethodSale): ISaleAddModelPaymentMethod => {
  const basePaymentMethod: ISaleAddModelPaymentMethod = {
    payment_method_id: paymentMethod.paymentMethodId,
    type: paymentMethod.type,
    amount: roundMoney(paymentMethod.amount),
  };

  // Agregar campos opcionales si existen
  if (paymentMethod.receivedAmount !== undefined) {
    basePaymentMethod.received_amount = paymentMethod.receivedAmount;
  }
  
  if (paymentMethod.change !== undefined) {
    basePaymentMethod.change = paymentMethod.change;
  }

  return basePaymentMethod;
};

export const createSaleAddCreditDataAdapter = (creditData: ICreditDataSale): ISaleAddModelCreditData => ({
  fees_number: creditData.feesNumber,
  interest_rate_percentage: creditData.interestRatePercentage,
  payment_dates: creditData.paymentDates.map(createSaleAddPaymentDateAdapter)
});

export const createSaleAddPaymentDateAdapter = (paymentDate: ICreditDataDate): ISaleAddModelPaymentDate => ({
  date: paymentDate.date,
  fee_number: paymentDate.feeNumber
});

// =============================================================================
// ADAPTADOR INVERSO: SalesResponse → ISaleAddModel
// Usado para reenviar ventas offline guardadas al servidor
// =============================================================================

import { 
  SalesResponse, 
  Bill, 
  Item, 
  PaymentMethod 
} from "@/pages/[others]/pages/sales/models/sale.interface";

/**
 * Convierte una venta almacenada localmente (SalesResponse) al formato
 * que espera la API para crear ventas (ISaleAddModel).
 * 
 * IMPORTANTE: Se usa cuando necesitamos reenviar ventas offline al servidor.
 * La estructura de almacenamiento (SalesResponse) es diferente a la de envío (ISaleAddModel):
 * - SalesResponse tiene `bills: Bill[]` (array)
 * - ISaleAddModel espera `bill: ISaleAddModelBill` (objeto singular)
 */
export const createSaleAddFromResponse = (saleResponse: SalesResponse): ISaleAddModel => {
  const bill = saleResponse.bills?.[0]; // Tomamos el primer bill (generalmente solo hay uno)
  
  if (!bill) {
    throw new Error("No se encontró información del comprobante en la venta");
  }

  const payload: ISaleAddModel = {
    cashier: {
      id: saleResponse.cashier.id,
      name: saleResponse.cashier.name,
    },
    workshift: {
      id: saleResponse.workshift.id,
      name: saleResponse.workshift.name,
    },
    seller: {
      id: saleResponse.seller.id,
      name: saleResponse.seller.name,
      last_name: saleResponse.seller.lastname, // Note: lastname → last_name
    },
    bill: createBillFromResponse(bill),
    customer: {
      id: saleResponse.customer.id,
      document_number: saleResponse.customer.document_number,
    },
    additional_data: {
      document_type: "",
      document_number: "",
      purchase_order: "",
      apply_retention: false,
      apply_detraction: false,
      bank_account_number: "",
      detraction_percentage: "",
      send_whatsapp: false,
      whatsapp_number: "",
      send_email: false,
      email: "",
    },
    creation_date: saleResponse.creation_date,
    offline_uuid: saleResponse.offline_uuid ?? null,
  };
  normalizeSaleAddPayloadContainerTypes(
    payload,
    useProductStore.getState().products
  );
  return payload;
};

/**
 * Convierte un Bill de SalesResponse al formato ISaleAddModelBill
 */
type StoredBillType = Bill['bill_type'] & {
  documentName?: string;
  sunatDocumentType?: string;
  sunatDocumentNumber?: string;
};

const createBillFromResponse = (bill: Bill): ISaleAddModelBill => {
  const billType = bill.bill_type as StoredBillType;

  return {
  bill_type: {
    id: billType.id,
    name: billType.name,
    type: billType.type,
    ...(billType.documentName != null && { documentName: billType.documentName }),
    ...(billType.sunatDocumentType != null && {
      sunatDocumentType: billType.sunatDocumentType,
    }),
    ...(billType.sunatDocumentNumber != null &&
      billType.sunatDocumentType == null && {
        sunatDocumentType: billType.sunatDocumentNumber,
      }),
  },
  serial: bill.serial,
  correlative: parseInt(bill.correlative, 10) || 0,
  discount: parseFloat(bill.discount) || 0,
  discountType: bill.discountType || "",
  total_amount: typeof bill.total_amount === "string" 
    ? parseFloat(bill.total_amount) 
    : bill.total_amount,
  loyaltySummary: bill.loyalty_summary ?? {
    earned: 0,
    redeemed: 0,
    finalPoints: 0,
  },
  items: bill.items.map((item) => createItemFromResponse(item, bill)),
  payment_type: bill.payment_type,
  payment_methods: bill.payment_methods?.map(createPaymentMethodFromResponse) ?? null,
  credit_data: bill.credit_data ?? null,
};
};

/**
 * Convierte un Item de Bill al formato ISaleAddModelItem
 */
const createItemFromResponse = (item: Item, bill: Bill): ISaleAddModelItem => {
  const resolved = resolveItemDiscountFields(item, bill);

  return {
    product_id: item.product_id,
    lots: item.lots.map((lot: { id?: number | null; serial?: string; quantity?: number }) => ({
      id: lot.id ?? null,
      serial: lot.serial ?? "",
      quantity: lot.quantity ?? 0,
    })),
    name: item.name,
    container_type: {
      id: item.container_type.id,
      name: item.container_type.name,
    },
    unit_price: item.unit_price,
    total_quantity: item.total_quantity,
    total_price: item.total_price,
    vat_type: {
      id: item.vat_type.id,
      name: item.vat_type.name,
      type: item.vat_type.type,
    },
    discount_type: resolved.discount_type,
    discounted_amount: resolved.discounted_amount,
    ...(resolved.discount_amount != null && resolved.discount_amount > 0
      ? { discount_amount: resolved.discount_amount }
      : {}),
    points_redeemed: item.points_redeemed ?? 0,
    amount_redeemed: item.amount_redeemed ?? 0,
  };
};

function resolveItemDiscountFields(
  item: Item,
  bill: Bill
): {
  discount_type: string;
  discounted_amount: number;
  discount_amount?: number;
} {
  const discountType = item.discount_type ?? "";
  const discountedAmount = item.discounted_amount ?? 0;
  const discountAmount = item.discount_amount ?? 0;

  if (discountType) {
    const money =
      discountAmount > 0
        ? discountAmount
        : discountType === ITEM_DISCOUNT_BY_AMOUNT && discountedAmount > 0
          ? discountedAmount
          : undefined;

    return {
      discount_type:
        discountType === ITEM_DISCOUNT_BY_POINTS ? "" : discountType,
      discounted_amount: discountedAmount,
      discount_amount: money,
    };
  }

  // Ventas offline guardadas antes del fix: solo discount a nivel bill
  const billDiscount = parseFloat(String(bill.discount)) || 0;
  if (
    bill.discountType?.includes("product") &&
    billDiscount > 0 &&
    bill.items.length === 1
  ) {
    return {
      discount_type: ITEM_DISCOUNT_BY_AMOUNT,
      discounted_amount: billDiscount,
      discount_amount: billDiscount,
    };
  }

  return { discount_type: "", discounted_amount: 0 };
}

/**
 * Convierte un PaymentMethod al formato ISaleAddModelPaymentMethod
 */
const createPaymentMethodFromResponse = (pm: PaymentMethod): ISaleAddModelPaymentMethod => {
  const amount =
    typeof pm.amount === "string" ? parseFloat(pm.amount) : pm.amount;

  const base: ISaleAddModelPaymentMethod = {
    payment_method_id: pm.payment_method_id,
    type: pm.type,
    amount,
  };

  if (pm.received_amount !== undefined && pm.received_amount !== "") {
    base.received_amount =
      typeof pm.received_amount === "string"
        ? parseFloat(pm.received_amount)
        : pm.received_amount;
  }

  if (pm.change !== undefined && pm.change !== "") {
    base.change =
      typeof pm.change === "string" ? parseFloat(pm.change) : pm.change;
  }

  return base;
};